import {
  AccountStatusEnum,
  BillGroupErrorStatusEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { parseDateAsUtc, toDateShort } from '@monetize/utils/core';
import { addDays } from 'date-fns/addDays';
import { FC } from 'react';
import {
  MAlert,
  MLink,
  MSpinner,
  MText,
  MTooltip,
} from '../../../components/Monetize';
import { usePastInvoice } from './hooks/usePastInvoice';

/**
 * Bill group generate invoice banner
 * After generation, the banner goes away for 15 seconds, then may reappear if the next invoice date is still in the past
 */
export interface BillGroupGenerateInvoiceProps {
  billGroupId: string;
  nextInvoiceDate: string;
  billGroupStatus: string;
  accountId: string;
  accountStatus?: AccountStatusEnum;
  invoiceDelayDirty?: Maybe<boolean>;
  invoiceDelay?: Maybe<number>;
  errorStatus?: BillGroupErrorStatusEnum;
}

export const BillGroupGenerateInvoice: FC<BillGroupGenerateInvoiceProps> = ({
  billGroupId,
  nextInvoiceDate,
  billGroupStatus,
  accountId,
  accountStatus,
  invoiceDelayDirty = false,
  invoiceDelay = 0,
  errorStatus,
}: BillGroupGenerateInvoiceProps) => {
  const isAccountActive =
    !accountStatus || accountStatus === AccountStatusEnum.ACTIVE;
  const { canUpdate, hasPastInvoices, isLoading, generatePreviousInvoice } =
    usePastInvoice({
      accountId,
      billGroupId,
      billGroupStatus,
      nextInvoiceDate,
      invoiceDelay,
      invoiceDelayDirty,
    });

  if (!canUpdate || !hasPastInvoices) {
    return null;
  }

  if (errorStatus === BillGroupErrorStatusEnum.TAX_CALCULATION_ERROR) {
    return (
      <MAlert type="error">
        <MText>
          There is a tax calculation error on your most recent invoice.
        </MText>
      </MAlert>
    );
  }

  return (
    <MAlert type="info" fontWeight="500" fontSize="md" mb="4">
      This Bill Group has pending invoice(s). Next Invoice Date is{' '}
      {toDateShort(addDays(parseDateAsUtc(nextInvoiceDate), invoiceDelay ?? 0))}
      .{' '}
      <MTooltip
        label="Invoice generation is not available because the account is not Active. Update the account status to Active to enable invoice generation."
        isDisabled={isAccountActive}
        shouldWrapChildren
      >
        <MLink
          color={isAccountActive ? 'tIndigo.base' : 'tGray.darkPurple'}
          fontWeight="bold"
          pointerEvents={isAccountActive ? undefined : 'none'}
          onClick={() => {
            if (!isLoading && isAccountActive) {
              generatePreviousInvoice({ accountId, billGroupId });
            }
          }}
        >
          Generate
        </MLink>
      </MTooltip>
      {isLoading && <MSpinner size="sm" ml={2} />}
    </MAlert>
  );
};
